import { API_BASE_URL } from '@/config/serverConfig';

export const ACCOUNT_DEACTIVATED_CODE = 'ACCOUNT_DEACTIVATED';
export const ACCOUNT_DEACTIVATED_MESSAGE =
  'Ваш аккаунт отключён. Обратитесь к администратору.';

let authRedirectInFlight = false;
let forcedLogoutHandler: ((message: string) => Promise<void>) | null = null;
let pendingDeactivatedMessage: string | null = null;
let refreshInFlight: Promise<RefreshOutcome> | null = null;

export function consumePendingDeactivatedMessage(): string | null {
  const message = pendingDeactivatedMessage;
  pendingDeactivatedMessage = null;
  return message;
}

export async function requestServerLogout(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
  } catch {
    /* ignore */
  }
}

export function redirectToAuth(): void {
  if (window.location.pathname !== '/auth') {
    window.location.href = '/auth';
  }
}

export async function redirectToAuthAndClearSession(): Promise<void> {
  if (authRedirectInFlight) return;
  authRedirectInFlight = true;
  await requestServerLogout();
  redirectToAuth();
}

export function registerForcedLogoutHandler(handler: (message: string) => Promise<void>): void {
  forcedLogoutHandler = handler;
}

export function unregisterForcedLogoutHandler(): void {
  forcedLogoutHandler = null;
}

function isDeactivatedBody(body: unknown): body is { code?: string; error?: string } {
  if (!body || typeof body !== 'object') return false;
  const code = (body as { code?: string }).code;
  return code === ACCOUNT_DEACTIVATED_CODE;
}

/** true — сессия принудительно завершена, дальнейшая обработка ответа не нужна */
export async function handleForcedSessionEnd(status: number, body: unknown): Promise<boolean> {
  if (status !== 403 || !isDeactivatedBody(body)) return false;
  const message =
    typeof body.error === 'string' && body.error.trim()
      ? body.error.trim()
      : ACCOUNT_DEACTIVATED_MESSAGE;
  pendingDeactivatedMessage = message;
  if (forcedLogoutHandler) {
    await forcedLogoutHandler(message);
    return true;
  }
  await redirectToAuthAndClearSession();
  return true;
}

/**
 * Итог обновления пропуска: 'refreshed' — новые cookie выставлены; 'rejected' — сервер отказал (сессия
 * отозвана, истекла, аккаунт отключён); 'unavailable' — ответа нет (обрыв сети, смена Wi-Fi, перезапуск
 * сервера, 5xx прокси). Выход из аккаунта — только на 'rejected'.
 */
export type RefreshOutcome = 'refreshed' | 'rejected' | 'unavailable';

const REFRESH_LOCK_NAME = 'tracker:auth-refresh';
const REFRESH_TIMEOUT_MS = 15_000;
const REFRESH_RETRY_MIN_MS = 2_000;
const REFRESH_RETRY_MAX_MS = 60_000;

/** Ответ /auth/refresh → итог. 401/403 — отказ сервера; прочие ошибки и не-JSON (страница прокси) — сервер недоступен. */
export function refreshOutcomeFromResponse(status: number, body: unknown): RefreshOutcome {
  if (status === 401 || status === 403) return 'rejected';
  if (status < 200 || status >= 300) return 'unavailable';
  if (!body || typeof body !== 'object') return 'unavailable';
  return (body as { success?: unknown }).success === true ? 'refreshed' : 'rejected';
}

async function postRefresh(): Promise<RefreshOutcome> {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
    });
    const body: unknown = await res.json().catch(() => null);
    return refreshOutcomeFromResponse(res.status, body);
  } catch {
    return 'unavailable';
  }
}

/**
 * Обновить пропуск по refresh-cookie. Одна попытка на вкладку за раз; между вкладками — по очереди
 * (Web Locks): cookie общая, и вкладка, предъявившая уже заменённый токен, получила бы отказ.
 */
export async function tryRefreshAccessSession(): Promise<RefreshOutcome> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
      return locks ? await locks.request(REFRESH_LOCK_NAME, postRefresh) : await postRefresh();
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function waitForRetry(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener('online', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    window.addEventListener('online', done);
  });
}

/** Повторять attempt, пока сервер недоступен: пауза растёт до минуты, появление сети будит сразу. */
export async function retryWhileUnavailable<T, U extends T>(
  attempt: () => Promise<T>,
  isUnavailable: (result: T) => result is U
): Promise<Exclude<T, U>> {
  let delay = REFRESH_RETRY_MIN_MS;
  for (;;) {
    const result = await attempt();
    if (!isUnavailable(result)) return result as Exclude<T, U>;
    await waitForRetry(delay);
    delay = Math.min(delay * 2, REFRESH_RETRY_MAX_MS);
  }
}

const isRefreshUnavailable = (outcome: RefreshOutcome): outcome is 'unavailable' => outcome === 'unavailable';

/** Обновить пропуск, дождавшись ответа сервера: итог — только 'refreshed' или 'rejected'. */
export function refreshAccessSessionWhenReachable(): Promise<Exclude<RefreshOutcome, 'unavailable'>> {
  return retryWhileUnavailable(tryRefreshAccessSession, isRefreshUnavailable);
}

/**
 * Ответ 401/403 → что стало с сессией: 'ended' — завершена (выход); 'refreshed' — пропуск обновлён, запрос
 * можно повторить; 'kept' — сессию не трогали (не ошибка входа или сервер недоступен), запрос падает обычной ошибкой.
 */
export type AuthErrorOutcome = 'ended' | 'refreshed' | 'kept';

export async function resolveAuthErrorResponse(
  status: number,
  body: unknown,
  opts?: { skipRefresh?: boolean }
): Promise<AuthErrorOutcome> {
  if (await handleForcedSessionEnd(status, body)) return 'ended';
  if (status !== 401) return 'kept';
  if (!opts?.skipRefresh) {
    const outcome = await tryRefreshAccessSession();
    if (outcome === 'refreshed') return 'refreshed';
    if (outcome === 'unavailable') return 'kept';
  }
  await redirectToAuthAndClearSession();
  return 'ended';
}

/** Общая обработка 401 и деактивации для fetch/XHR без повтора запроса. true — сессия завершена. */
export async function handleAuthErrorResponse(
  status: number,
  body: unknown,
  opts?: { skipRefresh?: boolean }
): Promise<boolean> {
  return (await resolveAuthErrorResponse(status, body, opts)) === 'ended';
}

export type AuthDeviceSession = {
  id: string;
  platform: string;
  device_name: string | null;
  user_agent: string | null;
  ip: string | null;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  current: boolean;
};

export async function fetchAuthSessions(): Promise<AuthDeviceSession[]> {
  const res = await fetch(`${API_BASE_URL}/auth/sessions`, { credentials: 'include' });
  if (!res.ok) {
    if (res.status === 401) {
      if ((await tryRefreshAccessSession()) === 'refreshed') {
        const retry = await fetch(`${API_BASE_URL}/auth/sessions`, { credentials: 'include' });
        if (!retry.ok) return [];
        const data = (await retry.json()) as { sessions?: AuthDeviceSession[] };
        return Array.isArray(data.sessions) ? data.sessions : [];
      }
    }
    return [];
  }
  const data = (await res.json()) as { sessions?: AuthDeviceSession[] };
  return Array.isArray(data.sessions) ? data.sessions : [];
}

export async function revokeAuthSession(sessionId: string): Promise<boolean> {
  const res = await fetch(`${API_BASE_URL}/auth/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  return res.status === 204 || res.ok;
}

export async function logoutAllAuthSessions(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout-all`, { method: 'POST', credentials: 'include' });
  } catch {
    /* ignore */
  }
}
